import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API, timeout: 60000, withCredentials: true });

export const exchangeSession = (session_id) => api.post("/auth/session", { session_id }).then(r => r.data);
export const fetchMe = () => api.get("/auth/me").then(r => r.data);
export const updateMyTimezone = (timezone) => api.patch("/auth/me/timezone", { timezone }).then(r => r.data);
export const logout = () => api.post("/auth/logout").then(r => r.data);

// Admin (scheduler + delivery)
export const fetchSchedulerHealth = () => api.get("/admin/scheduler/health").then(r => r.data);
export const fetchDeliveryMetrics = () => api.get("/admin/delivery/metrics").then(r => r.data);
export const triggerSchedulerTick = () => api.post("/admin/scheduler/tick").then(r => r.data);

export const fetchDashboard = () => api.get("/dashboard").then(r => r.data);
export const fetchFamilyCalendar = (year, month, child_id) => {
  const p = new URLSearchParams();
  if (year) p.set("year", year);
  if (month) p.set("month", month);
  if (child_id) p.set("child_id", child_id);
  const qs = p.toString();
  return api.get(`/calendar/family${qs ? `?${qs}` : ""}`).then(r => r.data);
};
export const fetchFamily = () => api.get("/family").then(r => r.data);
export const fetchChildren = () => api.get("/children").then(r => r.data);
export const fetchChildIntelligence = (child_id) =>
  api.get(`/children/${child_id}/intelligence`).then(r => r.data);
export const generateChildInsight = (child_id) =>
  api.post(`/children/${child_id}/insight`, {}, { timeout: 90_000 }).then(r => r.data);
export const fetchChildMilestones = (child_id) =>
  api.get(`/children/${child_id}/milestones`).then(r => r.data);
export const fetchEmails = () => api.get("/emails").then(r => r.data);
export const fetchChildEmails = (params = {}) => api.get("/child-emails", { params }).then(r => r.data);
export const actChildEmail = (id, action) => api.patch(`/child-emails/${id}/action`, { action }).then(r => r.data);
export const fetchTasks = () => api.get("/tasks").then(r => r.data);
export const updateTask = (id, completed) => api.patch(`/tasks/${id}`, { completed }).then(r => r.data);
export const fetchCalendarSuggestions = () => api.get("/calendar-suggestions").then(r => r.data);
export const confirmCalendarSuggestion = (id) => api.post(`/calendar-suggestions/${id}/confirm`).then(r => r.data);
export const dismissCalendarSuggestion = (id) => api.post(`/calendar-suggestions/${id}/dismiss`).then(r => r.data);
export const fetchSenderRules = () => api.get("/sender-rules").then(r => r.data);
export const createSenderRule = (data) => api.post("/sender-rules", data).then(r => r.data);
export const deleteSenderRule = (id) => api.delete(`/sender-rules/${id}`).then(r => r.data);
export const joinWaitlist = (data) => api.post("/waitlist", data).then(r => r.data);
export const fetchSuggestedQuestions = () => api.get("/copilot/suggested-questions").then(r => r.data);
export const fetchDigest = () => api.get("/digest").then(r => r.data);
export const ingestEmail = (data) => api.post("/emails/ingest", data).then(r => r.data);
export const fetchGmailStatus = () => api.get("/gmail/status").then(r => r.data);
export const resetMyDemo = () => api.post("/demo/reset-mine").then(r => r.data);

export const submitFeedback = (data) => api.post("/feedback", data).then(r => r.data);
export const fetchMyFeedback = () => api.get("/feedback/mine").then(r => r.data);
export const fetchAdminAnalytics = () => api.get("/admin/analytics").then(r => r.data);
export const fetchAdminFeedback = () => api.get("/admin/feedback").then(r => r.data);

export const gmailConnect = () => api.get("/gmail/connect").then(r => r.data);
export const gmailStatus = () => api.get("/gmail/status").then(r => r.data);
export const gmailDisconnect = () => api.post("/gmail/disconnect").then(r => r.data);
export const gmailSync = (params = {}) =>
  // Long-running call — explicit 120s client timeout so we don't bail before
  // Claude finishes classifying the batch.
  api.post("/gmail/sync", null, { params, timeout: 120_000 }).then(r => r.data);
export const retryClassification = (limit = 50) => api.post("/emails/retry-classification", null, { params: { limit } }).then(r => r.data);

// Weekly Command Center
export const fetchBriefingCurrent = (params = {}) => api.get("/briefing/current", { params }).then(r => r.data);
export const fetchBriefingPreview = () => api.get("/briefing/preview").then(r => r.data);
export const fetchBriefingHistory = () => api.get("/briefing/history").then(r => r.data);
export const generateBriefing = (data = {}) => api.post("/briefing/generate", data).then(r => r.data);
export const emailMyBriefing = () => api.post("/briefing/email").then(r => r.data);

// Notification preferences + email log
export const fetchNotificationPreferences = () => api.get("/notifications/preferences").then(r => r.data);
export const updateNotificationPreferences = (data) => api.patch("/notifications/preferences", data).then(r => r.data);
export const fetchEmailLog = () => api.get("/email/log").then(r => r.data);

// Family Insights + Family Health Score
export const fetchTaskInsights = () => api.get("/insights/tasks").then(r => r.data);
export const fetchHealthScore = () => api.get("/health-score/current").then(r => r.data);
export const fetchHealthHistory = () => api.get("/health-score/history").then(r => r.data);

// Billing
export const fetchBillingPlans = () => api.get("/billing/plans").then(r => r.data);
export const fetchBillingMe = () => api.get("/billing/me").then(r => r.data);
export const createCheckoutSession = (plan_id) => api.post("/billing/checkout", { plan_id, origin_url: window.location.origin }).then(r => r.data);
export const fetchCheckoutStatus = (session_id) => api.get(`/billing/status/${session_id}`).then(r => r.data);
export const cancelSubscription = () => api.post("/billing/cancel").then(r => r.data);
export const startFreeTrial = () => api.post("/billing/start-trial").then(r => r.data);
export const createPortalSession = () => api.post("/billing/portal").then(r => r.data);
export const createChild = (data) => api.post("/children", data).then(r => r.data);
export const updateChild = (child_id, data) => api.patch(`/children/${child_id}`, data).then(r => r.data);
export const deleteChild = (child_id) => api.delete(`/children/${child_id}`).then(r => r.data);
export const loadDemoFamily = () => api.post("/demo/load").then(r => r.data);
export const clearDemoData = () => api.post("/demo/clear").then(r => r.data);
export const fetchBusinessMetrics = () => api.get("/admin/business/metrics").then(r => r.data);
export const fetchActivationMetrics = () => api.get("/admin/activation/metrics").then(r => r.data);

// Onboarding + subscriber events
export const fetchOnboardingChecklist = () => api.get("/onboarding/checklist").then(r => r.data);
export const dismissOnboarding = () => api.post("/onboarding/dismiss").then(r => r.data);
export const recordSubscriberEvent = (event_name) => api.post(`/subscriber/events/${event_name}`).then(r => r.data);

// Gmail Launch Prep
export const joinGmailWaitlist = (source = "connect_gmail_page") => api.post("/gmail/waitlist", { source }).then(r => r.data);
export const fetchGmailWaitlistMe = () => api.get("/gmail/waitlist/me").then(r => r.data);
export const fetchAdminWaitlistMetrics = () => api.get("/admin/waitlist/metrics").then(r => r.data);
export const fetchAdminReadiness = () => api.get("/admin/readiness").then(r => r.data);
export const notifyGmailWaitlist = () => api.post("/admin/gmail/notify_waitlist").then(r => r.data);
export const fetchGmailNotifications = () => api.get("/admin/gmail/notifications").then(r => r.data);

// Sprint H — Production Hardening
export const fetchAdminAlerts = (status = "open") => api.get("/admin/alerts", { params: { status } }).then(r => r.data);
export const resolveAdminAlert = (alert_id) => api.post(`/admin/alerts/${alert_id}/resolve`).then(r => r.data);
export const fetchAdminBackups = () => api.get("/admin/backups").then(r => r.data);
export const runBackupNow = () => api.post("/admin/backups/run").then(r => r.data);
export const fetchMemories = (window = "year") => api.get("/memories", { params: { window } }).then(r => r.data);
export const fetchMemoriesTimeline = (window = "year", child_id) =>
  api.get("/memories/timeline", { params: { window, child_id } }).then(r => r.data);
export const submitEmailFeedback = (email_id, parent_classification, opts = {}) =>
  api.post(`/emails/${email_id}/feedback`, {
    parent_classification,
    feedback_kind: opts.feedback_kind || null,
    category: opts.category || null,
    child_id: opts.child_id || null,
    notes: opts.notes || null,
  }).then(r => r.data);
export const getAutoSync = () => api.get("/gmail/auto-sync").then(r => r.data);
export const setAutoSync = (enabled) => api.patch("/gmail/auto-sync", { enabled }).then(r => r.data);
export const uploadEmail = (filename, content_b64) =>
  api.post("/emails/upload", { filename, content_b64 }, { timeout: 90_000 }).then(r => r.data);
export const fetchInboundAddress = () => api.get("/inbound/address").then(r => r.data);
export const regenerateInboundAddress = () => api.post("/inbound/address/regenerate").then(r => r.data);

// Sprint O Phase B — Google Calendar
export const fetchGoogleCalendarStatus = () => api.get("/calendar/google/status").then(r => r.data);
export const fetchGoogleCalendarEvents = (days = 30) =>
  api.get(`/calendar/google/events?days=${days}`).then(r => r.data);
export const setCalendarAutoAddTrusted = (enabled) =>
  api.patch("/calendar/google/auto-add-trusted", { enabled }).then(r => r.data);
export const pushEmailToGoogleCalendar = (email_id, overrides = {}) =>
  api.post(`/calendar/google/from-email/${email_id}`, overrides).then(r => r.data);
export const deleteGoogleCalendarEvent = (google_event_id) =>
  api.delete(`/calendar/google/event/${google_event_id}`).then(r => r.data);

// Sprint O Phase B — Attachments
export const uploadAttachment = (filename, content_b64, child_id) =>
  api.post("/attachments/upload", { filename, content_b64, child_id }, { timeout: 90_000 }).then(r => r.data);
export const fetchAttachments = (child_id) =>
  api.get(`/attachments${child_id ? `?child_id=${child_id}` : ""}`).then(r => r.data);

// Sprint O Phase B — Copilot
export const copilotAsk = (question, context, history) =>
  api.post("/copilot/ask", { question, context, history }, { timeout: 60_000 }).then(r => r.data);
export const copilotQuickActions = () => api.get("/copilot/quick-actions").then(r => r.data);

// Sprint P Phase 1 — Timeline lanes / Growth scores / Command Center / Search
export const fetchTimelineLanes = (window = "year", child_id, lane) => {
  const p = new URLSearchParams();
  if (window) p.set("window", window);
  if (child_id) p.set("child_id", child_id);
  if (lane) p.set("lane", lane);
  return api.get(`/memories/timeline-lanes?${p.toString()}`).then(r => r.data);
};
export const fetchChildScores = (child_id) =>
  api.get(`/children/${child_id}/scores`).then(r => r.data);
export const fetchCommandCenter = () =>
  api.get("/dashboard/command-center").then(r => r.data);
export const universalSearch = (q, types, limit = 20) => {
  const p = new URLSearchParams({ q });
  if (types) p.set("types", types);
  if (limit) p.set("limit", String(limit));
  return api.get(`/search?${p.toString()}`).then(r => r.data);
};

// Sprint Q — Recommendations / Copilot memory / Family story / Memory vault
export const fetchRecommendations = () => api.get("/recommendations").then(r => r.data);
export const saveCopilotConversation = (payload) =>
  api.post("/copilot/conversations", payload).then(r => r.data);
export const listCopilotConversations = (child_id, pinned_only = false) => {
  const p = new URLSearchParams();
  if (child_id) p.set("child_id", child_id);
  if (pinned_only) p.set("pinned_only", "true");
  return api.get(`/copilot/conversations?${p.toString()}`).then(r => r.data);
};
export const updateCopilotConversation = (id, body) =>
  api.patch(`/copilot/conversations/${id}`, body).then(r => r.data);
export const deleteCopilotConversation = (id) =>
  api.delete(`/copilot/conversations/${id}`).then(r => r.data);
export const fetchFamilyStory = (year, month, child_id) => {
  const p = new URLSearchParams();
  if (child_id) p.set("child_id", child_id);
  const qs = p.toString();
  return api.get(`/family-story/${year}/${month}${qs ? `?${qs}` : ""}`, { timeout: 90_000 }).then(r => r.data);
};
export const memoryVaultSearch = (q, types) =>
  api.get(`/memory-vault/search?q=${encodeURIComponent(q)}${types ? `&types=${types}` : ""}`).then(r => r.data);

// Sprint R — Family Legacy & Sharing Layer
export const familyStoryPdfUrl = (year, month, child_id) => {
  const p = new URLSearchParams();
  if (child_id) p.set("child_id", child_id);
  const qs = p.toString();
  return `${API}/family-story/${year}/${month}/pdf${qs ? `?${qs}` : ""}`;
};
export const shareFamilyStory = (year, month, payload, child_id) => {
  const p = new URLSearchParams();
  if (child_id) p.set("child_id", child_id);
  const qs = p.toString();
  return api.post(`/family-story/${year}/${month}/share${qs ? `?${qs}` : ""}`, payload).then(r => r.data);
};
export const fetchYearbook = (year, child_id) => {
  const p = new URLSearchParams();
  if (child_id) p.set("child_id", child_id);
  const qs = p.toString();
  return api.get(`/yearbook/${year}${qs ? `?${qs}` : ""}`, { timeout: 60_000 }).then(r => r.data);
};
export const yearbookPdfUrl = (year, child_id) => {
  const p = new URLSearchParams();
  if (child_id) p.set("child_id", child_id);
  const qs = p.toString();
  return `${API}/yearbook/${year}/pdf${qs ? `?${qs}` : ""}`;
};
export const fetchLegacy = () => api.get("/legacy").then(r => r.data);
export const fetchChildJourney = (child_id) =>
  api.get(`/children/${child_id}/journey`).then(r => r.data);
export const memoryVaultAsk = (q) =>
  api.get(`/memory-vault/ask?q=${encodeURIComponent(q)}`, { timeout: 30_000 }).then(r => r.data);

// Sprint S — Beta Readiness & Usage Intelligence
export const trackEvent = (event_type, meta) =>
  api.post("/track", { event_type, meta: meta || {} }, { timeout: 5_000 })
    .then(r => r.data)
    .catch(() => null); // analytics must never break the UI
export const submitWidgetFeedback = ({ surface, vote, target_id, comment }) =>
  api.post("/feedback/widget", { surface, vote, target_id, comment }).then(r => r.data);
export const fetchAdminUsage    = (days = 30) => api.get(`/admin/usage?days=${days}`).then(r => r.data);
export const fetchAdminAdoption = ()           => api.get("/admin/adoption").then(r => r.data);
export const fetchAdminWidgetFeedback = (days = 30) => api.get(`/admin/feedback/widget?days=${days}`).then(r => r.data);
export const fetchAdminHealth   = (days = 30) => api.get(`/admin/family-health?days=${days}`).then(r => r.data);
export const fetchAdminFounder  = (days = 30) => api.get(`/admin/founder?days=${days}`).then(r => r.data);

// Stripe subscriptions (Flow A — real sandbox)
export const fetchSubPricing        = ()               => api.get("/subscriptions/pricing").then(r => r.data);
export const fetchMySubscription    = ()               => api.get("/subscriptions/me").then(r => r.data);
export const startSubCheckout       = (lookup_key)     => api.post("/subscriptions/checkout",
  { lookup_key, origin_url: window.location.origin }).then(r => r.data);
export const openStripePortal       = ()               => api.post("/subscriptions/portal",
  { origin_url: window.location.origin }).then(r => r.data);
export const pollPaymentStatus      = (session_id)     => api.get(`/payments/status/${session_id}`).then(r => r.data);

// LLM Router (admin — Sprint T followup)
export const fetchLlmSettings   = ()                       => api.get("/admin/llm-settings").then(r => r.data);
export const patchLlmSetting    = (feature, model_key)     => api.patch("/admin/llm-settings", { feature, model_key }).then(r => r.data);
